import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Download, TrendingUp, Package, DollarSign, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { fetchSourcePerformance, SourcePerformanceMetrics } from '@/services/unifiedSourcePerformanceService';
import * as XLSX from 'xlsx';
import Layout from '@/components/layout/Layout';

const DATE_PRESETS = [
  { label: 'This Month', value: 'this-month' },
  { label: 'Last Month', value: 'last-month' },
  { label: 'Last 3 Months', value: 'last-3' },
  { label: 'Last 6 Months', value: 'last-6' },
  { label: 'Last 12 Months', value: 'last-12' },
];

const SOURCE_TYPE_OPTIONS = [
  { label: 'All Sources', value: 'all' },
  { label: 'Suppliers', value: 'supplier' },
  { label: 'Agents', value: 'agent' },
  { label: 'Farms', value: 'farm' },
  { label: 'Coordinators', value: 'harvest' },
];

const PIE_COLORS = ['hsl(var(--primary))', 'hsl(var(--destructive))', 'hsl(142 71% 45%)', 'hsl(38 92% 50%)'];

const sourceTypeBadgeVariant = (type: string) => {
  switch (type) {
    case 'supplier': return 'default';
    case 'agent': return 'secondary';
    case 'farm': return 'outline';
    case 'harvest': return 'destructive';
    default: return 'default';
  }
};

const sourceTypeLabel = (type: string) => {
  switch (type) {
    case 'supplier': return 'Supplier';
    case 'agent': return 'Agent';
    case 'farm': return 'Farm';
    case 'harvest': return 'Coordinator';
    default: return type;
  }
};

function getDateRange(preset: string): { start: string; end: string } {
  const now = new Date();
  let start: Date;
  switch (preset) {
    case 'this-month':
      start = startOfMonth(now);
      return { start: format(start, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
    case 'last-month':
      start = startOfMonth(subMonths(now, 1));
      return { start: format(start, 'yyyy-MM-dd'), end: format(endOfMonth(subMonths(now, 1)), 'yyyy-MM-dd') };
    case 'last-3':
      start = startOfMonth(subMonths(now, 3));
      return { start: format(start, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
    case 'last-6':
      start = startOfMonth(subMonths(now, 6));
      return { start: format(start, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
    case 'last-12':
    default:
      start = startOfMonth(subMonths(now, 12));
      return { start: format(start, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
  }
}

type SortKey = 'sourceName' | 'orderCount' | 'totalVolume' | 'totalSpend' | 'avgOrderValue' | 'frequencyPerMonth' | 'completionRate' | 'rejectionRate';

export default function SourcePerformanceReportPage() {
  const [datePreset, setDatePreset] = useState('last-3');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('totalSpend');
  const [sortAsc, setSortAsc] = useState(false);

  const { start, end } = useMemo(() => getDateRange(datePreset), [datePreset]);

  const { data, isLoading } = useQuery({
    queryKey: ['source-performance', start, end, sourceTypeFilter],
    queryFn: () => fetchSourcePerformance(start, end, sourceTypeFilter),
  });

  const sortedMetrics = useMemo(() => {
    if (!data?.metrics) return [];
    return [...data.metrics].sort((a, b) => {
      const av = a[sortKey] as number | string;
      const bv = b[sortKey] as number | string;
      if (typeof av === 'string') return sortAsc ? (av as string).localeCompare(bv as string) : (bv as string).localeCompare(av as string);
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [data?.metrics, sortKey, sortAsc]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const top10Volume = useMemo(() =>
    [...(data?.metrics || [])].sort((a, b) => b.totalVolume - a.totalVolume).slice(0, 10),
    [data?.metrics]
  );

  const top10Spend = useMemo(() =>
    [...(data?.metrics || [])].sort((a, b) => b.totalSpend - a.totalSpend).slice(0, 10),
    [data?.metrics]
  );

  const pieData = useMemo(() => {
    if (!data?.metrics) return [];
    const byType = new Map<string, number>();
    data.metrics.forEach(m => byType.set(m.sourceType, (byType.get(m.sourceType) || 0) + m.totalSpend));
    return Array.from(byType.entries()).map(([name, value]) => ({ name: sourceTypeLabel(name), value }));
  }, [data?.metrics]);

  const exportToExcel = () => {
    if (!sortedMetrics.length) return;
    const rows = sortedMetrics.map(m => ({
      'Source Name': m.sourceName,
      'Type': sourceTypeLabel(m.sourceType),
      'Orders': m.orderCount,
      'Volume (kg)': m.totalVolume.toFixed(1),
      'Spend (ETB)': m.totalSpend.toFixed(2),
      'Avg Order Value': m.avgOrderValue.toFixed(2),
      'Frequency/Month': m.frequencyPerMonth.toFixed(1),
      'Completion %': m.completionRate.toFixed(1),
      'Rejection %': m.rejectionRate.toFixed(1),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Source Performance');
    XLSX.writeFile(wb, `source-performance-${start}-to-${end}.xlsx`);
  };

  const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  const fmtCurrency = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'ETB', maximumFractionDigits: 0 }).replace('ETB', 'ETB ');

  const sortIcon = (key: SortKey) => sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '';

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Source Performance Report</h1>
            <p className="text-sm text-muted-foreground">Procurement performance across all source types</p>
          </div>
          <Button variant="outline" size="sm" onClick={exportToExcel} disabled={!sortedMetrics.length}>
            <Download className="h-4 w-4 mr-2" /> Export Excel
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sourceTypeFilter} onValueChange={setSourceTypeFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOURCE_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Users className="h-3.5 w-3.5" /> Total Sources</div>
            <p className="text-2xl font-bold text-foreground">{data?.summary.totalSources ?? '–'}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Package className="h-3.5 w-3.5" /> Total Volume</div>
            <p className="text-2xl font-bold text-foreground">{data ? fmt(data.summary.totalVolume) + ' kg' : '–'}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><DollarSign className="h-3.5 w-3.5" /> Total Spend</div>
            <p className="text-2xl font-bold text-foreground">{data ? fmtCurrency(data.summary.totalSpend) : '–'}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><TrendingUp className="h-3.5 w-3.5" /> Avg Frequency</div>
            <p className="text-2xl font-bold text-foreground">{data ? fmt(data.summary.avgFrequency) + '/mo' : '–'}</p>
          </CardContent></Card>
        </div>

        {/* Ranked Table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Source Rankings</CardTitle></CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : sortedMetrics.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No procurement data found for this period.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('sourceName')}>Source{sortIcon('sourceName')}</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('orderCount')}>Orders{sortIcon('orderCount')}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('totalVolume')}>Volume (kg){sortIcon('totalVolume')}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('totalSpend')}>Spend (ETB){sortIcon('totalSpend')}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('avgOrderValue')}>Avg Order{sortIcon('avgOrderValue')}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('frequencyPerMonth')}>Freq/Mo{sortIcon('frequencyPerMonth')}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('completionRate')}>Completion{sortIcon('completionRate')}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('rejectionRate')}>Rejection{sortIcon('rejectionRate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedMetrics.map((m, i) => (
                    <TableRow key={m.sourceId + m.sourceType}>
                      <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="font-medium">{m.sourceName}</TableCell>
                      <TableCell><Badge variant={sourceTypeBadgeVariant(m.sourceType) as any}>{sourceTypeLabel(m.sourceType)}</Badge></TableCell>
                      <TableCell className="text-right">{m.orderCount}</TableCell>
                      <TableCell className="text-right">{fmt(m.totalVolume)}</TableCell>
                      <TableCell className="text-right">{fmt(m.totalSpend)}</TableCell>
                      <TableCell className="text-right">{fmt(m.avgOrderValue)}</TableCell>
                      <TableCell className="text-right">{m.frequencyPerMonth.toFixed(1)}</TableCell>
                      <TableCell className="text-right">{m.completionRate.toFixed(0)}%</TableCell>
                      <TableCell className="text-right">{m.rejectionRate.toFixed(0)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 by Volume (kg)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top10Volume} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="sourceName" width={75} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => fmt(v) + ' kg'} />
                  <Bar dataKey="totalVolume" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 by Spend (ETB)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top10Spend} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="sourceName" width={75} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => fmtCurrency(v)} />
                  <Bar dataKey="totalSpend" fill="hsl(var(--accent))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {pieData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Spend Distribution by Source Type</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={110} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => fmtCurrency(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
